import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, ArrowDownCircle, CalendarDays, Check, FileSpreadsheet, FileText,
  Layers, Paperclip, Pencil, Split, Trash2, Upload, Wallet, X,
} from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import api, { downloadExcel, fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import ExpenseDialog from "../components/ExpenseDialog";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { fmtDate, fmtMoney, todayISO } from "../utils/format";

const PALETTE = ["#0891B2", "#059669", "#D97706", "#7C3AED", "#0284C7", "#E11D48", "#0D9488", "#64748B"];
const moneyTick = (v) => (Math.abs(v) >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v);

function KpiCard({ label, value, sub, tone = "text-slate-900", testid, onClick }) {
  return (
    <button onClick={onClick} className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md w-full" data-testid={testid}>
      <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-mono-num mt-1.5 text-xl font-bold ${tone}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
    </button>
  );
}

function ActionDialog({ title, open, onClose, onSubmit, children, submitLabel, testid }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">{children}</div>
        <DialogFooter><Button onClick={onSubmit} data-testid={testid}>{submitLabel}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({ vehicle_id: "", group: "", status: "", date_from: "", date_to: "", amount_min: "", amount_max: "", q: "" });
  const [page, setPage] = useState(0);
  const [modal, setModal] = useState(null); // refuse|cancel|pay|edit
  const [target, setTarget] = useState(null);
  const [comment, setComment] = useState("");
  const [payForm, setPayForm] = useState({ amount: "", account_id: "", date: todayISO() });
  const [editForm, setEditForm] = useState({ amount: "", description: "", motif: "" });
  const [batchOpen, setBatchOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [batch, setBatch] = useState({ common: { vehicle_id: "", date: todayISO(), supplier: "", invoice_ref: "", payment_method: "Espèces", account_id: "" }, items: [{ group: "Entretien", category: "", amount: "", description: "" }] });
  const [split, setSplit] = useState({ vehicle_ids: [], group: "Transport international", category: "Transport terrestre", date: todayISO(), total: "", description: "", supplier: "", mode: "equal", amounts: {}, account_id: "" });
  const [importRows, setImportRows] = useState(null);
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const PAGE_SIZE = 15;

  const load = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    api.get(`/expenses?${params}`).then((r) => { setRows(r.data); setPage(0); });
    api.get("/expenses/stats").then((r) => setStats(r.data));
  };

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);
  useEffect(() => { load(); }, [filters]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const groups = meta?.expense_groups || {};
  const totalFiltered = rows.reduce((s, e) => s + (e.amount_fcfa || 0), 0);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const view = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const doAction = async () => {
    try {
      if (modal === "refuse") await api.post(`/expenses/${target.id}/refuse`, { comment });
      if (modal === "cancel") await api.post(`/expenses/${target.id}/cancel`, { motif: comment });
      if (modal === "pay") await api.post(`/expenses/${target.id}/pay`, { amount: Number(payForm.amount), account_id: payForm.account_id || null, date: payForm.date });
      if (modal === "edit") await api.patch(`/expenses/${target.id}`, { amount: editForm.amount ? Number(editForm.amount) : undefined, description: editForm.description || undefined, motif: editForm.motif });
      toast.success("Opération enregistrée");
      setModal(null); setComment("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const approve = async (e) => {
    try {
      await api.post(`/expenses/${e.id}/approve`);
      toast.success(`${e.expense_number} approuvée`);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const del = async (e) => {
    if (!window.confirm(`Supprimer définitivement ${e.expense_number} (${e.category}) ?${e.status !== "En attente" ? " La contre-écriture de caisse sera générée si un compte a été débité." : ""}`)) return;
    try { await api.delete(`/expenses/${e.id}`); toast.success("Dépense supprimée"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  const submitBatch = async () => {
    const items = batch.items.filter((i) => i.amount && i.category);
    if (!items.length) return toast.error("Ajoutez au moins une ligne avec catégorie et montant");
    try {
      const r = await api.post("/expenses/batch", { common: { ...batch.common, vehicle_id: batch.common.vehicle_id || null, account_id: batch.common.account_id || null }, items: items.map((i) => ({ ...i, amount: Number(i.amount) })) });
      toast.success(`Facture multi-postes : ${r.data.created} lignes créées`);
      setBatchOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const submitSplit = async () => {
    if (split.vehicle_ids.length < 2) return toast.error("Sélectionnez au moins 2 véhicules");
    try {
      const r = await api.post("/expenses/split", { ...split, total: Number(split.total), account_id: split.account_id || null });
      toast.success(`Répartition créée : ${r.data.created} véhicules · ${fmtMoney(r.data.total)}`);
      setSplitOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const parseCsv = async (file) => {
    const text = await file.text();
    const delim = text.includes(";") ? ";" : ",";
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    const header = lines[0].toLowerCase().split(delim).map((h) => h.trim());
    const idx = (name) => header.findIndex((h) => h.includes(name));
    const rows = lines.slice(1).map((l) => {
      const c = l.split(delim);
      return { vehicule: c[idx("vehicule")]?.trim(), date: c[idx("date")]?.trim(), groupe: c[idx("groupe")]?.trim(), categorie: c[idx("categ")]?.trim(), montant: c[idx("montant")]?.trim(), fournisseur: c[idx("fourn")]?.trim(), description: c[idx("descript")]?.trim() };
    });
    setImporting(true);
    try {
      const r = await api.post("/expenses/import", { rows, dry_run: true });
      setImportRows(r.data);
    } catch (e) { toast.error(fmtErr(e)); } finally { setImporting(false); }
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const rows = importRows.rows.filter((r) => r.ok).map((r) => ({ vehicule: r.vehicle === "Charge générale" ? "" : r.vehicle, date: r.date, groupe: r.group, categorie: r.category, montant: r.amount, fournisseur: r.supplier }));
      const r = await api.post("/expenses/import", { rows, dry_run: false });
      toast.success(`${r.data.valid} dépense(s) importée(s)`);
      setImportOpen(false); setImportRows(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setImporting(false); }
  };

  const exportFiltered = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    const lines = ["Numero;Date;Vehicule;Groupe;Categorie;Fournisseur;Montant;Paye;Reste;Statut"];
    rows.forEach((e) => lines.push([e.expense_number, e.date, e.vehicle_label, e.group, e.category, e.supplier, Math.round(e.amount_fcfa), Math.round(e.amount_paid), Math.round(e.remaining), e.status].join(";")));
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "depenses-filtrees.csv";
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Dépenses</h1>
          <p className="mt-1 text-sm text-slate-500">Chaque dépense est justifiée, tracée et intégrée au coût de revient du véhicule.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExpenseDialog onSaved={load} triggerLabel="Nouvelle dépense" />
          <Button variant="outline" onClick={() => setBatchOpen(true)} data-testid="batch-expense-button"><Layers className="mr-1 h-4 w-4" /> Facture multi-postes</Button>
          <Button variant="outline" onClick={() => setSplitOpen(true)} data-testid="split-expense-button"><Split className="mr-1 h-4 w-4" /> Répartition</Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-expenses-button"><Upload className="mr-1 h-4 w-4" /> Import CSV</Button>
          <Button variant="outline" onClick={() => downloadExcel("/export/excel?type=depenses")} data-testid="export-expenses-excel"><FileSpreadsheet className="mr-1 h-4 w-4" /> Excel</Button>
          <Button variant="outline" onClick={exportFiltered} data-testid="export-expenses-csv"><FileText className="mr-1 h-4 w-4" /> CSV filtré</Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8" data-testid="expenses-kpis">
          <KpiCard label="Aujourd'hui" value={fmtMoney(stats.today)} testid="exp-today" />
          <KpiCard label="Cette semaine" value={fmtMoney(stats.week)} testid="exp-week" />
          <KpiCard label="Ce mois" value={fmtMoney(stats.month)} testid="exp-month" />
          <KpiCard label="Cette année" value={fmtMoney(stats.year)} testid="exp-year" />
          <KpiCard label="En attente" value={stats.pending_count} sub={fmtMoney(stats.pending_amount)} tone="text-amber-600" testid="exp-pending" onClick={() => setF("status", "En attente")} />
          <KpiCard label="Non payées" value={stats.unpaid_count} sub={fmtMoney(stats.unpaid_amount)} tone="text-red-600" testid="exp-unpaid" onClick={() => setF("status", "Approuvée")} />
          <KpiCard label="Sans justificatif" value={stats.no_receipt_count} tone="text-orange-600" testid="exp-no-receipt" />
          <KpiCard label="Budget véhicules" value={fmtMoney(stats.budget_used)} sub={`prévu ${fmtMoney(stats.budget_total)} · écart ${fmtMoney(stats.budget_gap)}`} tone={stats.budget_gap < 0 ? "text-red-600" : "text-emerald-600"} testid="exp-budget" />
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="expenses-chart-monthly">
            <h3 className="font-heading mb-3 text-base font-semibold text-slate-800">Dépenses mensuelles</h3>
            {stats.monthly?.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tickFormatter={moneyTick} tick={{ fontSize: 10 }} width={50} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="value" fill="#E11D48" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            ) : <p className="flex h-56 items-center justify-center text-sm text-slate-400">Aucune donnée sur la période</p>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="expenses-chart-group">
            <h3 className="font-heading mb-3 text-base font-semibold text-slate-800">Par catégorie</h3>
            {stats.by_group?.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.by_group} dataKey="value" nameKey="name" innerRadius={40} outerRadius={75} paddingAngle={2}>
                    {stats.by_group.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            ) : <p className="flex h-56 items-center justify-center text-sm text-slate-400">Aucune donnée sur la période</p>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="expenses-chart-vehicle">
            <h3 className="font-heading mb-3 text-base font-semibold text-slate-800">Top véhicules les plus coûteux</h3>
            {stats.by_vehicle?.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.by_vehicle} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tickFormatter={moneyTick} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={150} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="value" fill="#0891B2" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            ) : <p className="flex h-56 items-center justify-center text-sm text-slate-400">Aucune donnée sur la période</p>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="expenses-chart-supplier">
            <h3 className="font-heading mb-3 text-base font-semibold text-slate-800">Top fournisseurs</h3>
            {stats.by_supplier?.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.by_supplier} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tickFormatter={moneyTick} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={130} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="value" fill="#D97706" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            ) : <p className="flex h-56 items-center justify-center text-sm text-slate-400">Aucune donnée sur la période</p>}
          </div>
        </div>
      )}

      {stats?.budget_over?.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="budget-alerts">
          <p className="flex items-center gap-2 font-semibold text-red-700"><AlertTriangle className="h-4 w-4" /> Budgets dépassés</p>
          {stats.budget_over.map((b, i) => (
            <p key={i} className="mt-1 text-sm text-red-600">{b.vehicle} : dépassé de {fmtMoney(b.over)} ({fmtMoney(b.actual)} / {fmtMoney(b.budget)})</p>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-white p-4" data-testid="expenses-filters">
        <div><Label className="text-xs">Véhicule</Label>
          <Select value={filters.vehicle_id} onValueChange={(v) => setF("vehicle_id", v === "all" ? "" : v)}>
            <SelectTrigger className="w-52" data-testid="expense-filter-vehicle"><SelectValue placeholder="Tous" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous</SelectItem>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.internal_number} — {v.brand} {v.model}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Catégorie</Label>
          <Select value={filters.group} onValueChange={(v) => setF("group", v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="expense-filter-group"><SelectValue placeholder="Toutes" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Toutes</SelectItem>{Object.keys(groups).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Statut</Label>
          <Select value={filters.status} onValueChange={(v) => setF("status", v === "all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="expense-filter-status"><SelectValue placeholder="Tous" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous</SelectItem>{["En attente", "Approuvée", "Payée", "Partiellement payée", "Refusée", "Annulée"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Du</Label><Input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} data-testid="expense-filter-from" /></div>
        <div><Label className="text-xs">Au</Label><Input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} data-testid="expense-filter-to" /></div>
        <div><Label className="text-xs">Montant min</Label><Input type="number" className="w-32" value={filters.amount_min} onChange={(e) => setF("amount_min", e.target.value)} data-testid="expense-filter-min" /></div>
        <div><Label className="text-xs">Montant max</Label><Input type="number" className="w-32" value={filters.amount_max} onChange={(e) => setF("amount_max", e.target.value)} data-testid="expense-filter-max" /></div>
        <div className="flex-1 min-w-48"><Label className="text-xs">Recherche</Label><Input placeholder="N°, description, facture, fournisseur…" value={filters.q} onChange={(e) => setF("q", e.target.value)} data-testid="expense-filter-q" /></div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="expenses-table">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 text-sm">
          <span className="text-slate-500">{rows.length} dépense(s) — total affiché : <span className="font-mono-num font-bold text-slate-900">{fmtMoney(totalFiltered)}</span></span>
          {rows.length > PAGE_SIZE && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="expenses-prev">←</Button>
              <span className="text-xs text-slate-500">{page + 1}/{pages}</span>
              <Button size="sm" variant="outline" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid="expenses-next">→</Button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2.5">N°</th><th className="px-3 py-2.5">Date</th><th className="px-3 py-2.5">Véhicule</th>
                <th className="px-3 py-2.5">Catégorie</th><th className="px-3 py-2.5">Fournisseur</th>
                <th className="px-3 py-2.5 text-right">Montant</th><th className="px-3 py-2.5 text-right">Payé / Reste</th>
                <th className="px-3 py-2.5">Statut</th><th className="px-3 py-2.5">Justif.</th><th className="px-3 py-2.5">Par</th><th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {view.length === 0 && <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">Aucune dépense pour ces critères.</td></tr>}
              {view.map((e) => (
                <tr key={e.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60" data-testid={`expense-row-${e.expense_number}`}>
                  <td className="px-3 py-2 font-mono-num text-xs font-semibold">{e.expense_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="px-3 py-2">
                    {e.vehicle_id ? (
                      <button className="font-medium text-cyan-700 hover:underline text-left" onClick={() => navigate(`/vehicules/${e.vehicle_id}`)}>{e.vehicle_label}</button>
                    ) : <span className="text-slate-400 italic">Charge générale</span>}
                  </td>
                  <td className="px-3 py-2"><p className="text-xs text-slate-400">{e.group}</p><p className="font-medium">{e.category}</p></td>
                  <td className="px-3 py-2 text-slate-600">{e.supplier || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <p className="font-mono-num font-semibold">{fmtMoney(e.amount_fcfa)}</p>
                    {e.currency !== "FCFA" && <p className="text-xs text-slate-400">{Number(e.amount).toLocaleString("fr-FR")} {e.currency}</p>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <p className="font-mono-num text-emerald-600 text-xs">{fmtMoney(e.amount_paid)}</p>
                    {e.remaining > 0 && <p className="font-mono-num text-red-600 text-xs font-semibold">reste {fmtMoney(e.remaining)}</p>}
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={e.status} /></td>
                  <td className="px-3 py-2">
                    {e.receipt_attached ? (
                      <button onClick={async () => { const r = await api.get(`/expenses/${e.id}/receipt`); window.open(r.data.data, "_blank"); }}
                        className="text-cyan-600 hover:text-cyan-800" title="Voir le justificatif" data-testid={`receipt-${e.expense_number}`}><Paperclip className="h-4 w-4" /></button>
                    ) : <span className="text-slate-300" title="Sans justificatif">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{e.created_by}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-0.5">
                      {e.status === "En attente" && (
                        <>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" title="Approuver" onClick={() => approve(e)} data-testid={`approve-${e.expense_number}`}><Check className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" title="Refuser" onClick={() => { setTarget(e); setModal("refuse"); setComment(""); }} data-testid={`refuse-${e.expense_number}`}><X className="h-4 w-4" /></Button>
                        </>
                      )}
                      {["Approuvée", "Partiellement payée"].includes(e.status) && e.remaining > 0 && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-cyan-700" onClick={() => { setTarget(e); setPayForm({ amount: String(e.remaining), account_id: "", date: todayISO() }); setModal("pay"); }} data-testid={`pay-${e.expense_number}`}>Payer</Button>
                      )}
                      {!["Annulée", "Refusée"].includes(e.status) && (
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Modifier" onClick={() => { setTarget(e); setEditForm({ amount: String(e.amount), description: e.description || "", motif: "" }); setModal("edit"); }} data-testid={`edit-${e.expense_number}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      )}
                      {!["Annulée", "Refusée"].includes(e.status) && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-red-500" title="Annuler (contre-écriture)" onClick={() => { setTarget(e); setModal("cancel"); setComment(""); }} data-testid={`cancel-${e.expense_number}`}><ArrowDownCircle className="h-4 w-4" /></Button>
                      )}
                      {["En attente", "Brouillon"].includes(e.status) && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" title="Supprimer le brouillon" onClick={() => del(e)} data-testid={`delete-${e.expense_number}`}><X className="h-4 w-4" /></Button>
                      )}
                      {isAdmin && !["En attente", "Brouillon"].includes(e.status) && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" title="Supprimer (admin)" onClick={() => del(e)} data-testid={`hard-delete-${e.expense_number}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ActionDialog open={modal === "refuse"} onClose={() => setModal(null)} title={`Refuser ${target?.expense_number}`} onSubmit={doAction} submitLabel="Confirmer le refus" testid="refuse-submit">
        <div><Label>Motif du refus</Label><Textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="refuse-comment" /></div>
      </ActionDialog>
      <ActionDialog open={modal === "cancel"} onClose={() => setModal(null)} title={`Annuler ${target?.expense_number} (contre-écriture)`} onSubmit={doAction} submitLabel="Confirmer l'annulation" testid="cancel-expense-submit">
        <p className="text-sm text-slate-500">L'opération restera dans l'historique. Si un compte a été débité, une contre-écriture sera générée.</p>
        <div><Label>Motif de l'annulation *</Label><Textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="cancel-motif" /></div>
      </ActionDialog>
      <ActionDialog open={modal === "pay"} onClose={() => setModal(null)} title={`Payer ${target?.expense_number} — reste ${fmtMoney(target?.remaining)}`} onSubmit={doAction} submitLabel="Valider le paiement" testid="pay-expense-submit">
        <div><Label>Montant (FCFA)</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-expense-amount" /></div>
        <div><Label>Compte débité</Label>
          <Select value={payForm.account_id} onValueChange={(v) => setPayForm({ ...payForm, account_id: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="pay-expense-account"><SelectValue placeholder="Aucun" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </ActionDialog>
      <ActionDialog open={modal === "edit"} onClose={() => setModal(null)} title={`Modifier ${target?.expense_number}`} onSubmit={doAction} submitLabel="Enregistrer la modification" testid="edit-expense-submit">
        <div><Label>Nouveau montant</Label><Input type="number" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} data-testid="edit-expense-amount" /></div>
        <div><Label>Description</Label><Input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
        <div><Label>Motif de la modification *</Label><Input value={editForm.motif} onChange={(e) => setEditForm({ ...editForm, motif: e.target.value })} placeholder="Ex : correction facture fournisseur" data-testid="edit-expense-motif" /></div>
      </ActionDialog>

      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Facture multi-postes</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Véhicule</Label>
              <Select value={batch.common.vehicle_id} onValueChange={(v) => setBatch({ ...batch, common: { ...batch.common, vehicle_id: v } })}>
                <SelectTrigger data-testid="batch-vehicle"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.internal_number} — {v.brand} {v.model}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={batch.common.date} onChange={(e) => setBatch({ ...batch, common: { ...batch.common, date: e.target.value } })} /></div>
            <div><Label>Fournisseur</Label><Input value={batch.common.supplier} onChange={(e) => setBatch({ ...batch, common: { ...batch.common, supplier: e.target.value } })} data-testid="batch-supplier" /></div>
            <div><Label>N° facture</Label><Input value={batch.common.invoice_ref} onChange={(e) => setBatch({ ...batch, common: { ...batch.common, invoice_ref: e.target.value } })} /></div>
            <div><Label>Compte</Label>
              <Select value={batch.common.account_id} onValueChange={(v) => setBatch({ ...batch, common: { ...batch.common, account_id: v === "none" ? "" : v } })}>
                <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {batch.items.map((item, i) => (
              <div key={i} className="grid grid-cols-12 items-end gap-2 rounded-lg border border-slate-100 bg-slate-50/50 p-2">
                <div className="col-span-3"><Label className="text-xs">Catégorie</Label>
                  <Select value={item.group} onValueChange={(v) => { const items = [...batch.items]; items[i] = { ...item, group: v, category: "" }; setBatch({ ...batch, items }); }}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.keys(groups).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-3"><Label className="text-xs">Sous-catégorie</Label>
                  <Select value={item.category} onValueChange={(v) => { const items = [...batch.items]; items[i] = { ...item, category: v }; setBatch({ ...batch, items }); }}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`batch-category-${i}`}><SelectValue placeholder="…" /></SelectTrigger>
                    <SelectContent>{(groups[item.group] || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="text-xs">Montant</Label><Input type="number" className="h-8" value={item.amount} data-testid={`batch-amount-${i}`} onChange={(e) => { const items = [...batch.items]; items[i] = { ...item, amount: e.target.value }; setBatch({ ...batch, items }); }} /></div>
                <div className="col-span-3"><Label className="text-xs">Description</Label><Input className="h-8" value={item.description} onChange={(e) => { const items = [...batch.items]; items[i] = { ...item, description: e.target.value }; setBatch({ ...batch, items }); }} /></div>
                <div className="col-span-1"><Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => setBatch({ ...batch, items: batch.items.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button></div>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setBatch({ ...batch, items: [...batch.items, { group: "Entretien", category: "", amount: "", description: "" }] })} data-testid="batch-add-line">+ Ajouter une ligne</Button>
          </div>
          <p className="font-mono-num text-sm font-bold text-right">Total facture : {fmtMoney(batch.items.reduce((s, i) => s + (Number(i.amount) || 0), 0))}</p>
          <DialogFooter><Button onClick={submitBatch} data-testid="batch-submit">Enregistrer la facture</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={splitOpen} onOpenChange={setSplitOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Répartition d'une dépense entre plusieurs véhicules</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Montant total (FCFA)</Label><Input type="number" value={split.total} onChange={(e) => setSplit({ ...split, total: e.target.value })} data-testid="split-total" /></div>
            <div><Label>Date</Label><Input type="date" value={split.date} onChange={(e) => setSplit({ ...split, date: e.target.value })} /></div>
            <div><Label>Catégorie</Label>
              <Select value={split.group} onValueChange={(v) => setSplit({ ...split, group: v, category: "" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(groups).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Sous-catégorie</Label>
              <Select value={split.category} onValueChange={(v) => setSplit({ ...split, category: v })}>
                <SelectTrigger><SelectValue placeholder="…" /></SelectTrigger>
                <SelectContent>{(groups[split.group] || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description</Label><Input value={split.description} onChange={(e) => setSplit({ ...split, description: e.target.value })} placeholder="Ex : transport groupé de 4 véhicules" /></div>
            <div className="col-span-2"><Label>Mode de répartition</Label>
              <Select value={split.mode} onValueChange={(v) => setSplit({ ...split, mode: v })}>
                <SelectTrigger data-testid="split-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="equal">Répartition égale</SelectItem><SelectItem value="custom">Répartition personnalisée</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-100 p-2">
            {vehicles.filter((v) => v.status !== "Vendu").map((v) => (
              <label key={v.id} className="flex items-center gap-3 rounded px-2 py-1.5 hover:bg-slate-50 text-sm">
                <input type="checkbox" className="h-4 w-4 accent-cyan-600" data-testid={`split-vehicle-${v.internal_number}`}
                  checked={split.vehicle_ids.includes(v.id)}
                  onChange={(e) => setSplit({ ...split, vehicle_ids: e.target.checked ? [...split.vehicle_ids, v.id] : split.vehicle_ids.filter((x) => x !== v.id) })} />
                <span className="flex-1">{v.internal_number} — {v.brand} {v.model}</span>
                {split.vehicle_ids.includes(v.id) && (
                  split.mode === "equal"
                    ? <span className="font-mono-num text-xs text-slate-500">{fmtMoney((Number(split.total) || 0) / split.vehicle_ids.length)}</span>
                    : <Input type="number" className="h-7 w-32" placeholder="Montant" value={split.amounts[v.id] || ""} onChange={(e) => setSplit({ ...split, amounts: { ...split.amounts, [v.id]: e.target.value } })} />
                )}
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={submitSplit} data-testid="split-submit">Répartir la dépense</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportRows(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Import de dépenses (CSV / Excel)</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Format attendu (en-tête) : <code className="bg-slate-100 px-1 rounded">vehicule;date;groupe;categorie;montant;fournisseur;description</code> — le véhicule est identifié par n° interne, VIN ou immatriculation. Vide = charge générale.</p>
          {!importRows ? (
            <Input type="file" accept=".csv,.txt" data-testid="import-file-input" onChange={(e) => e.target.files?.[0] && parseCsv(e.target.files[0])} />
          ) : (
            <>
              <p className="text-sm font-semibold">{importRows.valid}/{importRows.total} lignes valides{importRows.errors > 0 && <span className="text-red-600"> — {importRows.errors} erreur(s)/doublon(s)</span>}</p>
              <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-100">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 text-left"><th className="px-2 py-1.5">Ligne</th><th className="px-2 py-1.5">Véhicule</th><th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Catégorie</th><th className="px-2 py-1.5 text-right">Montant</th><th className="px-2 py-1.5">État</th></tr></thead>
                  <tbody>
                    {importRows.rows.map((r) => (
                      <tr key={r.line} className={`border-t ${r.ok ? "" : "bg-red-50"}`}>
                        <td className="px-2 py-1.5">{r.line}</td><td className="px-2 py-1.5">{r.vehicle}</td><td className="px-2 py-1.5">{r.date}</td>
                        <td className="px-2 py-1.5">{r.group} / {r.category}</td><td className="px-2 py-1.5 text-right font-mono-num">{fmtMoney(r.amount)}</td>
                        <td className="px-2 py-1.5">{r.ok ? <span className="text-emerald-600 font-semibold">OK</span> : <span className="text-red-600">{r.errors.join(" · ")}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            {importRows && <Button onClick={confirmImport} disabled={importing || !importRows.valid} data-testid="import-confirm">{importing ? "Import…" : `Importer ${importRows.valid} dépense(s)`}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
